<template>
  <Teleport to="#teleport-wrapper">
    <div data-login-popin>
      <AkModal
        ref="akAuthPopin"
        data-testid="login-popin-wrapper-modal"
        class="login-popin-modal login-max-size"
        size="max"
        :with-padding="false"
        :with-closer="true"
        @cancelled="closeAuthPopinModal"
      >
        <div data-testid="login-popin">
          <AuthPopin
            v-if="state.props"
            :view="state.props.view"
            :initial-email="state.props.email"
            :audience="state.props.audience"
            :verification-token="state.props.verificationToken"
            :success-callback="state.props.successCallback"
            :brand-id="state.props.data?.brandId"
            :is-reminded="state.props.data?.isReminded"
            :brand-name="state.props.data?.brandName"
            :lift-code="state.props.data?.liftCode"
            :in-page="false"
          />
        </div>
      </AkModal>
    </div>
  </Teleport>
</template>

<script lang="ts" setup>
import { watch, nextTick, useTemplateRef } from 'vue';
import AuthPopin from '@monolith/legacy/components/popins/auth-popin.vue';
import { AkModal } from '@ankorstore/design-system';
import { useAuthPopinState } from '@core/composables/use-auth-popin-state';

// The wrapper is purely the modal host for programmatic opens (openAuthPopin
// from CTAs). Funnel deep links are real pages now — /register and
// /brand-registration render the signup email form inline (SEL-26) — so the
// legacy #register / #brand-register hash entries are gone.
const akAuthPopin = useTemplateRef<InstanceType<typeof AkModal>>('akAuthPopin');

const { state, closeAuthPopinModal } = useAuthPopinState();

watch(
  () => state.props,
  (props) => {
    if (props) {
      nextTick(() => {
        akAuthPopin.value?.openModal();
      });
    }
  },
);
</script>

<style lang="scss">
@import '@css/vue-import';

// AkModal paints at 1040 and drawers at 1050 (modules/shared/drawer/drawer-wrapper.vue), which
// leaves the popin underneath any open drawer. Promoting this host div to its own stacking context
// lifts the whole popin above them without fighting AkModal's own higher-specificity z-index rule.
[data-login-popin] {
  position: relative;
  z-index: 1060;
}

.login-popin-modal {
  -webkit-overflow-scrolling: touch;
  @apply ds-max-h-full ds-w-full;

  .ak-modal__closer {
    @apply ds-z-10 ds-top-2;
  }

  &.login-max-size {
    .ak-modal__container {
      @apply ds-p-[16px] ds-py-[32px] ds-w-full md:ds-p-[64px] md:ds-w-[528px] md:ds-max-w-[528px];
    }
  }

  // The referral variant of the signup form needs a wider shell. These two rules and the
  // overflow fix below used to live in email-sign-up-form.vue, but they style the modal
  // this component owns, and that form now ships inside the lazy sign-up chunk — so the
  // login, reset-password, 2FA and email-validation views would never have loaded them.
  &.login-max-size:has(.referral-header) {
    .ak-modal__container {
      @apply md:ds-w-[580px] md:ds-max-w-[580px];
    }

    .ak-modal__closer {
      @apply ds-top-2 ds-right-2;
    }
  }

  .ak-modal__container {
    @apply ds-h-full md:ds-h-auto;
    // Fix 100vw overflow on small screens.
    max-width: 100vw;
    overflow-x: hidden;
  }
}
</style>
